/*
 * Serialised cart writes.
 *
 * Shopify's cart endpoints read the cart, modify it and write it back. Concurrent
 * mutations therefore clobber one another: clicking four Add to Cart buttons in quick
 * succession produced four 200 OK responses and a cart containing three items, with the
 * first one silently lost.
 *
 * Every cart write in the theme goes through this queue so only one is ever in flight.
 * Optimistic UI is unaffected — the painting stays immediate, only the network is
 * serialised.
 */
(function () {
  'use strict';

  var chain = Promise.resolve();

  window.bigSkyCartQueue = function (task) {
    var run = chain.then(task, task);
    /* Keep the chain alive after a rejection, or one failure stalls every later write. */
    chain = run.then(function () {}, function () {});
    return run;
  };
})();

/*
 * Shared cart badge writer.
 *
 * Lives here rather than in big-sky-card-scripts because that snippet only renders on
 * collection pages, which meant a quantity change animated there and snapped everywhere
 * else. This file is loaded from layout/theme.liquid on every page.
 *
 * The pop is a CSS animation, not GSAP: GSAP is only pulled in by the collection grid,
 * and the badge should behave identically with or without it.
 */
(function () {
  'use strict';

  window.bigSkyCartBadge = function (count, animate) {
    document.querySelectorAll('.big-sky-header__cart-count, [data-cart-count]').forEach(function (el) {
      var shown = parseInt(el.textContent, 10) || 0;
      var wasHidden = el.style.display === 'none' || shown === 0;
      if (count === shown && !wasHidden) return;

      if (count === 0) {
        el.classList.remove('is-bumped');
        el.style.display = 'none';
        el.textContent = '0';
        return;
      }

      el.textContent = count;
      el.style.display = '';

      if (!animate) return;
      /* Restart the animation even when it is already running: remove, force a reflow,
         re-add. Without the reflow the browser coalesces both class changes and nothing
         replays. */
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  };
})();

/*
 * Big Sky cart drawer — step 1.
 *
 * Opens on cart icon click only. Never on add-to-cart: the client rejected auto-open
 * explicitly, and most orders are 4+ items so it would fire repeatedly.
 *
 * Contents are re-fetched through the Section Rendering API on every open, so an AJAX
 * quick-add elsewhere on the page cannot leave the drawer showing stale items. Liquid
 * stays the source of truth — no cart state is tracked here.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  var overlay = document.querySelector('[data-cart-drawer-overlay]');
  if (!drawer || !overlay) return;

  var SECTION_ID = 'big-sky-cart-drawer';
  var triggerElement = null;
  var isOpen = false;

  function getFocusableElements() {
    return drawer.querySelectorAll(
      'a[href]:not([tabindex="-1"]), button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
  }

  /* body { overflow: hidden } does not actually stop the page here, because the
     scrolling element is <html>, not body — verified the page still scrolled 400px with
     the drawer open. Taking body out of flow does stop it; the offset preserves the
     reading position and is restored on close. */
  var scrollLockY = 0;

  function lockScroll() {
    scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (otherOverlayOpen()) return;

    /* html carries scroll-behavior: smooth, so the restore below would ANIMATE —
       you watch the page scroll back to a position it never visually left, which
       reads as the page moving under you on close. Forced instant for the one
       frame the restore takes, then put back so in-page anchor links keep their
       smooth scrolling. Set inline on documentElement to outrank the stylesheet. */
    var root = document.documentElement;
    var previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, scrollLockY);

    root.style.scrollBehavior = previousBehavior;
  }

  /* Other overlays in this theme set body overflow without reference counting, so
     clearing it blindly would unlock the page while the mobile menu is still open. */
  function otherOverlayOpen() {
    var menu = document.querySelector('.big-sky-mobile-menu.is-open');
    var popup = document.getElementById('notify-popup');
    var search = document.querySelector('.big-sky-search-overlay.is-open');
    return !!(menu || search || (popup && popup.style.display === 'flex'));
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* FLIP. Record where every line sits, let the mutation happen, then animate each one
     from where it was to where it now is. Lines are matched by their cart line key, so
     this survives the section swap replacing the actual elements.

     Positions come from offsetTop, not getBoundingClientRect: the rect includes any
     transform currently applied, so measuring while an earlier animation is still in
     flight produced a fake delta, which animated, which corrupted the next measurement.
     Changing a quantity twice inside the animation window made the rows visibly
     re-shuffle even though the layout had not moved at all. offsetTop is layout only. */
  function captureLinePositions() {
    var map = {};
    drawer.querySelectorAll('.big-sky-cart-drawer-item').forEach(function (el) {
      map[el.getAttribute('data-line-key')] = el.offsetTop;
    });
    return map;
  }

  function playLineTransitions(previous, suppressEntrance) {
    if (reduceMotion.matches) return;

    var moved = 0;
    drawer.querySelectorAll('.big-sky-cart-drawer-item').forEach(function (el) {
      var key = el.getAttribute('data-line-key');
      var before = previous[key];
      var after = el.offsetTop;

      /* Never let two transforms stack on one row. */
      el.getAnimations().forEach(function (a) { a.cancel(); });

      /* A line that was not there before is new — slot it in rather than slide it.
         Suppressed when the swap is only replacing a line already shown optimistically,
         which would otherwise play the entrance twice. */
      if (before === undefined) {
        if (suppressEntrance) return;
        el.animate(
          [
            { opacity: 0, transform: 'translateY(-6px) scale(0.98)' },
            { opacity: 1, transform: 'none' }
          ],
          { duration: 260, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'backwards' }
        );
        return;
      }

      var delta = before - after;
      if (!delta) return;

      /* Staggered by position so the remaining lines read as re-sorting into place
         rather than snapping as a block. */
      el.animate(
        [{ transform: 'translateY(' + delta + 'px)' }, { transform: 'none' }],
        {
          duration: 320,
          delay: moved * 45,
          easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
          fill: 'backwards'
        }
      );
      moved++;
    });
  }

  function swapContents(html) {
    if (!html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-cart-drawer-contents]');
    var current = drawer.querySelector('[data-cart-drawer-contents]');
    if (!fresh || !current) return;

    /* The meter is re-rendered by Liquid on every swap, so the replacement element
       would paint straight at its final width with no transition. Carry the previous
       width across and let it animate to the new one. */
    var oldFill = current.querySelector('[data-meter-fill]');
    var previousWidth = oldFill ? oldFill.style.width : null;
    var linePositions = captureLinePositions();

    current.innerHTML = fresh.innerHTML;

    /* Anything watching the cart needs to know the markup underneath it was
       just replaced — the small-cart fork in particular, which is re-rendered
       by Liquid on every swap and loses its open state with the old nodes. */
    document.dispatchEvent(new CustomEvent('big-sky:cart-updated'));

    playLineTransitions(linePositions, suppressNextEntrance);
    suppressNextEntrance = false;

    setTimeout(updateRail, 0);

    var newFill = current.querySelector('[data-meter-fill]');
    if (newFill && previousWidth && previousWidth !== newFill.style.width) {
      var target = newFill.style.width;
      newFill.style.transition = 'none';
      newFill.style.width = previousWidth;
      void newFill.offsetWidth;
      newFill.style.transition = '';
      newFill.style.width = target;
    }
  }

  function updateBadges(itemCount, animate) {
    window.bigSkyCartBadge(itemCount, animate !== false);
  }

  /* Learn the shop's money format from one Liquid-rendered sample (123456789 cents),
     so optimistic prices match what the server will send back — no hardcoded currency,
     separator or symbol placement. */
  var money = (function () {
    var sample = drawer.getAttribute('data-money-sample') || '$1,234,567.89';
    var digits = sample.match(/[\d.,\s]*\d/);
    var numeric = digits ? digits[0] : '1,234,567.89';
    var prefix = sample.slice(0, sample.indexOf(numeric));
    var suffix = sample.slice(sample.indexOf(numeric) + numeric.length);
    /* In the sample the decimal part is always 89, so whatever precedes it is the
       decimal separator and the other recurring symbol groups the thousands. */
    var decSep = numeric.charAt(numeric.length - 3);
    var hasDecimals = decSep === '.' || decSep === ',';
    var thouSep = hasDecimals
      ? (decSep === '.' ? (numeric.indexOf(',') > -1 ? ',' : '') : (numeric.indexOf('.') > -1 ? '.' : ''))
      : (numeric.indexOf(',') > -1 ? ',' : numeric.indexOf('.') > -1 ? '.' : '');

    function format(cents, dropWholeDecimals) {
      var showDecimals = hasDecimals && !(dropWholeDecimals && cents % 100 === 0);
      var units = showDecimals ? (cents / 100).toFixed(2) : String(Math.round(cents / 100));
      var parts = units.split('.');
      if (thouSep) parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
      return prefix + parts.join(showDecimals ? decSep : '') + suffix;
    }
    format.short = function (cents) { return format(cents, true); };
    return format;
  })();

  /* Desired quantities the user has expressed but the server has not confirmed.
     Keyed by line key so several lines coalesce into one request. */
  var desired = {};
  var flushTimer = null;
  var inFlight = false;
  var FLUSH_DELAY = 300;

  function rowFor(key) {
    return drawer.querySelector('[data-line-key="' + key + '"]');
  }

  /* Paint the change immediately. The server response is still authoritative and will
     overwrite this a moment later; this only removes the wait from under the finger. */
  function applyOptimistic(key, qty) {
    var row = rowFor(key);
    if (!row) return;

    var count = row.querySelector('.big-sky-cart-drawer-item__count');
    if (count) count.textContent = qty;

    var unit = parseInt(row.getAttribute('data-unit-price'), 10);
    var price = row.querySelector('.big-sky-cart-drawer-item__price');
    if (price && !isNaN(unit)) price.textContent = money(unit * qty);

    var minus = row.querySelector('[data-step="-1"]');
    if (minus) minus.disabled = qty <= 1;

    previewTotals();
  }

  /* Badge counts units in the drawer plus any unconfirmed add. */
  function countUnits() {
    var total = pendingAddUnits;
    drawer.querySelectorAll('.big-sky-cart-drawer-item').forEach(function (r) {
      var c = r.querySelector('.big-sky-cart-drawer-item__count');
      total += c ? parseInt(c.textContent, 10) || 0 : 0;
    });
    return total;
  }

  var pendingAddUnits = 0;

  /* Recompute the meter and summary from what is currently on screen, so crossing the
     threshold lands on the same tap rather than a round trip later. This mirrors the
     Liquid in big-sky-shipping-meter.liquid; the server response re-renders both a
     moment later and remains authoritative. Copy is never built here — both states are
     already in the DOM and this only toggles them. */
  /* Cents for an ADD that has been shown but not yet confirmed by the server. The new
     row cannot be built here without duplicating Liquid, so the figures move
     immediately and the row itself arrives with the swap. */
  var pendingAddCents = 0;
  var suppressNextEntrance = false;

  /* null until the first run, so opening an already-unlocked cart does not pulse. */
  var wasUnlocked = null;

  function previewTotals() {
    var subtotal = pendingAddCents;
    drawer.querySelectorAll('.big-sky-cart-drawer-item').forEach(function (r) {
      var unit = parseInt(r.getAttribute('data-unit-price'), 10);
      var c = r.querySelector('.big-sky-cart-drawer-item__count');
      var qty = c ? parseInt(c.textContent, 10) || 0 : 0;
      if (!isNaN(unit)) subtotal += unit * qty;
    });

    /* A cart-level discount code lives inside the server's total but not in the unit
       prices above, so take it off here too. A percentage follows the quantity; a fixed
       amount holds. The server re-render corrects any rounding. */
    var bar = drawer.querySelector('.big-sky-cart-drawer__bar');
    var discountPct = bar ? parseFloat(bar.getAttribute('data-discount-pct')) || 0 : 0;
    var discountAmt = bar ? parseInt(bar.getAttribute('data-discount-amount'), 10) || 0 : 0;
    var discount = discountPct > 0 ? Math.round((subtotal * discountPct) / 100) : discountAmt;
    discount = Math.min(discount, subtotal);
    subtotal -= discount;
    drawer.querySelectorAll('[data-summary-discount]').forEach(function (el) {
      el.textContent = '\u2212' + money(discount);
    });

    var meter = drawer.querySelector('[data-shipping-meter]');
    var threshold = meter ? parseInt(meter.getAttribute('data-threshold'), 10) : 0;
    var shipRate = meter ? parseInt(meter.getAttribute('data-ship-rate'), 10) : 0;
    var unlocked = threshold > 0 && subtotal >= threshold;

    if (meter && threshold > 0) {
      var locked = meter.querySelector('[data-meter-locked]');
      var unlockedEl = meter.querySelector('[data-meter-unlocked]');
      /* Class rather than the hidden attribute so the two states can cross-fade. */
      if (locked) locked.classList.toggle('is-active', !unlocked);
      if (unlockedEl) unlockedEl.classList.toggle('is-active', unlocked);

      var remaining = meter.querySelector('[data-meter-remaining]');
      if (remaining) remaining.textContent = money.short(Math.max(0, threshold - subtotal));

      var fill = drawer.querySelector('[data-meter-fill]');
      var t = Math.min(100, Math.round((subtotal * 100) / threshold));
      if (fill) fill.style.width = Math.max(9, t) + '%';

      /* One custom property drives the bar, the amount in the message and the shipping
         figure in the footer, so they travel through the blues together. Set on the
         contents wrapper as well as the meter, since the footer sits outside it. */
      var colour = unlocked
        ? '#2E9BE6'
        : 'rgb(' + (11 + Math.floor((t * 35) / 100)) + ',' +
                   (34 + Math.floor((t * 121) / 100)) + ',' +
                   (64 + Math.floor((t * 166) / 100)) + ')';
      var contents = drawer.querySelector('[data-cart-drawer-contents]');
      if (contents) contents.style.setProperty('--big-sky-meter-colour', colour);
      meter.style.setProperty('--big-sky-meter-colour', colour);
    }

    /* Two places carry the subtotal now: the scrolling summary and the pinned bar. */
    drawer.querySelectorAll('[data-summary-subtotal]').forEach(function (el) {
      el.textContent = money(subtotal);
    });

    var paid = drawer.querySelector('[data-ship-paid]');
    var free = drawer.querySelector('[data-ship-free]');
    if (paid) paid.hidden = unlocked;
    if (free) free.hidden = !unlocked;

    drawer.querySelectorAll('[data-summary-total]').forEach(function (el) {
      el.textContent = money(unlocked ? subtotal : subtotal + shipRate);
    });

    updateBadges(countUnits(), false);

    /* Crossing into free shipping pulses FREE in the footer. That row is now the only
       place the saving is stated, so the crossing has to point at it. Only on the
       transition, never on a re-render that happens to be in the same state. */
    if (wasUnlocked !== null && unlocked && !wasUnlocked) {
      var freeEl = drawer.querySelector('.big-sky-cart-drawer__free');
      if (freeEl) {
        freeEl.classList.remove('is-pulsing');
        void freeEl.offsetWidth;
        freeEl.classList.add('is-pulsing');
      }
    }
    wasUnlocked = unlocked;
  }

  function flush() {
    if (inFlight) return;
    var updates = desired;
    if (!Object.keys(updates).length) return;
    desired = {};
    inFlight = true;

    window.bigSkyCartQueue(function () {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: SECTION_ID,
          sections_url: window.location.pathname
        })
      })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart update failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        /* If more clicks arrived while this was in flight, this response is already
           stale — skip the swap and let the next flush be authoritative, so the DOM
           never snaps backwards under the user. */
        if (Object.keys(desired).length) return;
        swapContents(cart.sections && cart.sections[SECTION_ID]);
        updateBadges(cart.item_count, false);
      })
      .catch(function (e) {
        console.warn('[big-sky-cart-drawer]', e);
        desired = {};
        return refreshContents();
      })
      .then(function () {
        inFlight = false;
        if (Object.keys(desired).length) flush();
      });
    });
  }

  function queueChange(key, qty) {
    desired[key] = qty;
    applyOptimistic(key, qty);
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, FLUSH_DELAY);
  }

  /* Removal is deliberate rather than incidental, so it goes immediately instead of
     waiting out the debounce. */
  function removeLine(key) {
    var linePositions = captureLinePositions();
    var row = rowFor(key);
    if (row) row.remove();
    playLineTransitions(linePositions);
    previewTotals();
    delete desired[key];
    desired[key] = 0;
    clearTimeout(flushTimer);
    flush();
  }

  function refreshContents() {
    var target = drawer.querySelector('[data-cart-drawer-contents]');
    if (!target) return Promise.resolve();

    var url = window.location.pathname + '?sections=' + SECTION_ID;
    /* Deliberately no Accept header. This endpoint sends `vary: Accept`, and asking for
       application/json returns a representation rendered outside the cart session — an
       empty cart, which would then overwrite the correct server-rendered contents. It
       returns JSON either way. */
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Section fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        swapContents(data[SECTION_ID]);
      })
      .catch(function (e) {
        /* Leave whatever was server-rendered in place rather than blanking the drawer. */
        console.warn('[big-sky-cart-drawer]', e);
      });
  }

  function open(fromElement) {
    if (isOpen) return;
    isOpen = true;
    triggerElement = fromElement || document.activeElement;

    if (window.bigSkyFunnel) {
      window.bigSkyFunnel.step('4_opened_cart', { surface: 'drawer' });
    }

    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();

    refreshContents();
    /* Seed the unlocked state from what is already rendered. Left until the user's
       first tap, that tap is usually the crossing itself, and the pulse was skipped on
       the one crossing that matters most. */
    previewTotals();
    setTimeout(updateRail, 0);

    setTimeout(function () {
      var closeBtn = drawer.querySelector('[data-cart-drawer-close]');
      if (closeBtn) closeBtn.focus();
    }, 50);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();

    if (triggerElement) {
      triggerElement.focus();
      triggerElement = null;
    }
  }

  /* Rail shows how far along the row you are. The peeking next card and this rail are
     the only affordances — no arrows, no dots. */
  function updateRail() {
    var track = drawer.querySelector('[data-popular-track]');
    var rail = drawer.querySelector('[data-popular-rail]');
    if (!track || !rail || !track.scrollWidth) return;
    var max = track.scrollWidth - track.clientWidth;
    var w = Math.max(18, Math.round((track.clientWidth / track.scrollWidth) * 100));
    rail.style.width = w + '%';
    rail.style.marginLeft = (max > 0 ? (track.scrollLeft / max) * (100 - w) : 0) + '%';
  }

  /* Scroll fires on the track, which is replaced on every swap, so listen on the
     drawer and filter. */
  drawer.addEventListener(
    'scroll',
    function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-popular-track')) updateRail();
    },
    true
  );

  /* Drag to scroll the suggestions. The brief asks for swipe in addition to scroll, and
     a mouse has no obvious way to move a horizontal strip — on desktop there was no
     discoverable way to reach the cards past the first. Arrows would contradict the
     brief's "no arrows, no dots", so this is drag plus the existing position rail. */
  var drag = null;

  drawer.addEventListener('pointerdown', function (e) {
    var track = e.target.closest && e.target.closest('[data-popular-track]');
    if (!track || e.button !== 0) return;
    drag = { track: track, startX: e.clientX, startLeft: track.scrollLeft, moved: false };
  });

  window.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var dx = e.clientX - drag.startX;
    /* A few pixels of slop so a tap on ADD is still a tap. */
    if (!drag.moved && Math.abs(dx) > 4) {
      drag.moved = true;
      drag.track.classList.add('is-dragging');
    }
    if (drag.moved) {
      drag.track.scrollLeft = drag.startLeft - dx;
      updateRail();
      e.preventDefault();
    }
  });

  function endDrag() {
    if (!drag) return;
    var track = drag.track;
    var moved = drag.moved;
    track.classList.remove('is-dragging');
    drag = null;
    if (moved) {
      /* Swallow the click that follows a drag, or releasing over a card fires ADD. */
      var swallow = function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
      };
      track.addEventListener('click', swallow, true);
      setTimeout(function () {
        track.removeEventListener('click', swallow, true);
      }, 0);
    }
    updateRail();
  }

  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', endDrag);

  /* Adding from inside an open drawer is the one case where the cart changes while the
     panel is visible, so this swaps contents rather than waiting for the next open. */
  /* Clone the Liquid template, fill it from the suggestion card that was clicked, and
     put it at the top of the list. The real row replaces it when the section comes
     back. */
  function insertOptimisticRow(button) {
    var tpl = drawer.querySelector('[data-line-template]');
    var items = drawer.querySelector('.big-sky-cart-drawer__items');
    var card = button && button.closest ? button.closest('.big-sky-popular__card') : null;
    if (!tpl || !items || !card) return false;

    var positions = captureLinePositions();
    var row = tpl.content.firstElementChild.cloneNode(true);

    var cardImg = card.querySelector('.big-sky-popular__thumb img');
    var rowImg = row.querySelector('.big-sky-cart-drawer-item__image');
    if (cardImg && rowImg) rowImg.src = cardImg.src;
    else if (rowImg) rowImg.remove();

    var href = (card.querySelector('.big-sky-popular__title') || {}).href || '';
    row.querySelectorAll('a').forEach(function (a) { a.href = href; });

    /* Carry the unit price on the row itself, so previewTotals and countUnits treat it
       exactly like a real line. Without this the row was counted by countUnits and its
       price added separately, and the badge read one too high until the swap. */
    var price = parseInt(button.getAttribute('data-price'), 10);
    if (!isNaN(price)) row.setAttribute('data-unit-price', price);

    var title = card.querySelector('.big-sky-popular__title');
    var rowTitle = row.querySelector('.big-sky-cart-drawer-item__title');
    if (title && rowTitle) rowTitle.textContent = title.textContent.trim();

    var price = card.querySelector('.big-sky-popular__price');
    var rowPrice = row.querySelector('.big-sky-cart-drawer-item__price');
    if (price && rowPrice) rowPrice.textContent = price.textContent.trim();

    items.insertBefore(row, items.firstChild);
    playLineTransitions(positions);
    return true;
  }

  function addVariant(variantId, button) {
    var price = button ? parseInt(button.getAttribute('data-price'), 10) : 0;
    var card = button && button.closest ? button.closest('.big-sky-popular__card') : null;

    /* Immediate: the card leaves the strip, and the badge, meter and totals move as if
       the item were already in. Previously this waited on /cart/add.js and then a
       second /cart.js before anything changed. */
    /* Insert before removing the card, so the card is still there to read from. */
    var inserted = insertOptimisticRow(button);
    suppressNextEntrance = inserted;

    /* The inserted row accounts for itself. The counters are only the fallback for when
       there is no template or card to build from. */
    if (!inserted) {
      if (!isNaN(price)) pendingAddCents += price;
      pendingAddUnits += 1;
    }

    if (card) card.remove();

    previewTotals();
    updateRail();

    window.bigSkyCartQueue(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          sections: SECTION_ID,
          sections_url: window.location.pathname
        })
      })
      .then(function (r) {
        if (!r.ok) throw new Error('Add failed: ' + r.status);
        return r.json();
      })
      .then(function (res) {
        pendingAddCents = 0;
        pendingAddUnits = 0;
        swapContents(res.sections && res.sections[SECTION_ID]);
        /* Recomputes badge, meter and totals from the fresh markup, so no separate
           /cart.js round trip is needed just to learn the item count. */
        previewTotals();
        updateRail();
      })
      .catch(function (e) {
        console.warn('[big-sky-cart-drawer]', e);
        pendingAddCents = 0;
        pendingAddUnits = 0;
        suppressNextEntrance = false;
        return refreshContents();
      });
    });
  }

  /* Progressive enhancement: both cart anchors keep their href to /cart, so with
     JavaScript off the icon still navigates. The mobile anchor carries no id, so bind
     by the shared class across both. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('.big-sky-header__cart-btn');
    if (trigger) {
      e.preventDefault();
      open(trigger);
      return;
    }
    if (e.target.closest && e.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }
    if (e.target === overlay) {
      close();
      return;
    }

    /* Delegated, because the contents are replaced wholesale on every change —
       directly bound handlers would not survive the swap. */
    var step = e.target.closest && e.target.closest('[data-qty-change]');
    if (step && !step.disabled) {
      var row = step.closest('.big-sky-cart-drawer-item');
      var countEl = row && row.querySelector('.big-sky-cart-drawer-item__count');
      var current = countEl ? parseInt(countEl.textContent, 10) || 1 : 1;
      var next = current + parseInt(step.getAttribute('data-step'), 10);
      if (next < 1) return; // stepper floors at 1; Remove is the only way out
      queueChange(row.getAttribute('data-line-key'), next);
      return;
    }

    var remove = e.target.closest && e.target.closest('[data-line-remove]');
    if (remove && !remove.disabled) {
      removeLine(remove.getAttribute('data-line-key'));
      return;
    }

    var add = e.target.closest && e.target.closest('[data-popular-add]');
    if (add && !add.disabled) {
      addVariant(add.getAttribute('data-variant-id'), add);
    }
  });

  /* Anything else on the page that mutates the cart can call this so the drawer is
     already correct by the time it is opened, rather than showing stale contents until
     the on-open refresh lands. */
  window.bigSkyCartDrawerRefresh = function () {
    return refreshContents().then(function () {
      previewTotals();
      updateRail();
    });
  };

  /* Deliberate exception to "no auto-open": the product page. Adding from there is a
     considered act on a page with no other cart affordance, and before this existed the
     form posted and navigated to /cart — so the drawer, and the free shipping meter with
     it, never appeared at all. Quick-add from a grid still stays closed; that is the
     repetitive case the no-auto-open decision was about. */
  window.bigSkyCartDrawerOpen = function (fromElement) {
    open(fromElement);
  };

  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      close();
      return;
    }

    if (e.key === 'Tab') {
      var focusable = getFocusableElements();
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
})();
